import { useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreateKitCorrectionRequestSchema, type LabKitDto } from '@ctms/contracts';
import { AlertTriangle, CheckCircle2, Scale, X } from 'lucide-react';

import { getApiErrorMessage } from '../../../api/client.js';
import { kitsApi } from '../kits.api.js';
import { kitQueryKeys } from '../kits.query-keys.js';

export interface StockCorrectionsDialogProps {
  trialId: number;
  /** kits:write — hides the correction form when false (register stays visible). */
  canWrite: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PAGE_SIZE = 10;

/**
 * Signed stock corrections (REQ-KIT-033): numberOfKits > 0 raises the stock,
 * < 0 lowers it, zero is rejected by the contract schema with a Polish message.
 */
export function StockCorrectionsDialog({
  trialId,
  canWrite,
  open,
  onOpenChange,
}: StockCorrectionsDialogProps): JSX.Element | null {
  const queryClient = useQueryClient();

  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [labKitId, setLabKitId] = useState<number | ''>('');
  const [labKitExpirationId, setLabKitExpirationId] = useState<number | ''>('');
  const [numberOfKits, setNumberOfKits] = useState<number | ''>('');
  const [correctionDate, setCorrectionDate] = useState(new Date().toISOString().slice(0, 10));
  const [comments, setComments] = useState('');
  const [page, setPage] = useState(1);

  const laboratoriesQuery = useQuery({
    queryKey: kitQueryKeys.laboratories.byTrial(trialId),
    queryFn: () => kitsApi.listLaboratories({ trialId, page: 1, limit: 100 }),
    enabled: open && canWrite && trialId > 0,
    staleTime: 5 * 60 * 1000,
  });
  const laboratories = laboratoriesQuery.data?.items ?? [];
  const labIds = laboratories.map((l) => l.id);

  const kitsQuery = useQuery({
    queryKey: ['kits', 'by-trial-all-labs', trialId, labIds],
    queryFn: async (): Promise<LabKitDto[]> => {
      const allKits: LabKitDto[] = [];
      for (const lab of laboratories) {
        const kitsPage = await kitsApi.listKits({
          trialLaboratoryId: lab.id,
          page: 1,
          limit: 100,
        });
        allKits.push(...kitsPage.items);
      }
      return allKits;
    },
    enabled: open && canWrite && laboratories.length > 0,
    staleTime: 5 * 60 * 1000,
  });
  const availableKits = kitsQuery.data ?? [];

  const selectedKit = availableKits.find((k) => k.id === labKitId);

  const expirationsQuery = useQuery({
    queryKey: kitQueryKeys.kits.expirations(labKitId === '' ? 0 : labKitId),
    queryFn: () => kitsApi.listExpirations(labKitId as number),
    enabled: labKitId !== '' && selectedKit?.expiration === true,
    staleTime: 5 * 60 * 1000,
  });
  const expirations = expirationsQuery.data ?? [];

  const registerQuery = useQuery({
    queryKey: kitQueryKeys.corrections.list({ page, limit: PAGE_SIZE, trialId }),
    queryFn: () => kitsApi.listCorrections({ page, limit: PAGE_SIZE, trialId }),
    enabled: open && trialId > 0,
    staleTime: 60 * 1000,
  });
  const corrections = registerQuery.data?.items ?? [];
  const total = registerQuery.data?.total ?? 0;
  const hasNext = registerQuery.data?.hasNext ?? false;

  const createMutation = useMutation({
    mutationFn: async (payload: Parameters<typeof kitsApi.createCorrection>[0]) =>
      kitsApi.createCorrection(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kitQueryKeys.corrections.all });
      queryClient.invalidateQueries({ queryKey: kitQueryKeys.stock.all });
      resetForm();
      setNotice('Korekta stanu zarejestrowana.');
    },
    onError: (err: unknown) => {
      void getApiErrorMessage(err, 'Nie udało się zarejestrować korekty').then(setFormError);
    },
  });

  function resetForm(): void {
    setLabKitId('');
    setLabKitExpirationId('');
    setNumberOfKits('');
    setCorrectionDate(new Date().toISOString().slice(0, 10));
    setComments('');
    setFormError(null);
  }

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    setFormError(null);
    setNotice(null);
    const parsed = CreateKitCorrectionRequestSchema.safeParse({
      labKitId,
      labKitExpirationId: labKitExpirationId === '' ? null : labKitExpirationId,
      numberOfKits,
      correctionDate,
      comments: comments.trim() || undefined,
    });
    if (!parsed.success) {
      const firstIssue = parsed.error.issues[0];
      setFormError(firstIssue ? firstIssue.message : 'Nieprawidłowe dane formularza');
      return;
    }
    createMutation.mutate(parsed.data);
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="stock-corrections-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-xs"
    >
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-xl border border-slate-700 bg-slate-900 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-950 text-amber-400 border border-amber-800/60">
              <Scale className="h-5 w-5" />
            </div>
            <div>
              <h2 id="stock-corrections-title" className="text-lg font-semibold text-slate-100">
                Korekty stanu magazynowego
              </h2>
              <p className="text-xs text-slate-400">
                Podpisane korekty inwentaryzacyjne — dodatnie zwiększają, ujemne obniżają stan
                (REQ-KIT-033)
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
            aria-label="Zamknij"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {notice && (
            <div
              role="status"
              className="flex items-center gap-2 rounded-lg border border-emerald-900/60 bg-emerald-950/40 p-3 text-sm text-emerald-300"
            >
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              <span>{notice}</span>
            </div>
          )}

          {canWrite && (
            <form
              id="stock-correction-form"
              noValidate
              onSubmit={handleSubmit}
              className="space-y-4 rounded-lg border border-slate-800 bg-slate-950/40 p-4"
            >
              <h3 className="text-sm font-semibold text-slate-200">Nowa korekta stanu</h3>
              {formError && (
                <div
                  role="alert"
                  className="flex items-start gap-3 rounded-lg border border-rose-900/60 bg-rose-950/40 p-3 text-sm text-rose-300"
                >
                  <AlertTriangle className="h-5 w-5 shrink-0 text-rose-400" />
                  <span>{formError}</span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <label className="block text-xs">
                  <span className="text-slate-400">Typ zestawu *</span>
                  <select
                    aria-label="Typ zestawu laboratoryjnego"
                    value={labKitId}
                    onChange={(e) => {
                      setLabKitId(e.target.value === '' ? '' : Number(e.target.value));
                      setLabKitExpirationId('');
                    }}
                    className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
                  >
                    <option value="">— wybierz —</option>
                    {availableKits.map((kit) => (
                      <option key={kit.id} value={kit.id}>
                        {kit.name}
                      </option>
                    ))}
                  </select>
                </label>
                {selectedKit?.expiration === true && (
                  <label className="block text-xs">
                    <span className="text-slate-400">Partia (data ważności)</span>
                    <select
                      aria-label="Partia zestawu"
                      value={labKitExpirationId}
                      onChange={(e) =>
                        setLabKitExpirationId(e.target.value === '' ? '' : Number(e.target.value))
                      }
                      className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
                    >
                      <option value="">— bez partii —</option>
                      {expirations.map((exp) => (
                        <option key={exp.id} value={exp.id}>
                          {exp.expirationDate}
                          {exp.batchComment ? ` — ${exp.batchComment}` : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <label className="block text-xs">
                  <span className="text-slate-400">Liczba kitów (±) *</span>
                  <input
                    type="number"
                    aria-label="Liczba kitów korekty"
                    placeholder="np. -2 lub 3"
                    value={numberOfKits}
                    onChange={(e) =>
                      setNumberOfKits(e.target.value === '' ? '' : Number(e.target.value))
                    }
                    className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
                  />
                  <span className="mt-1 block text-[11px] text-slate-500">
                    Dodatnia zwiększa stan, ujemna obniża. Zero jest odrzucane.
                  </span>
                </label>
                <label className="block text-xs">
                  <span className="text-slate-400">Data korekty *</span>
                  <input
                    type="date"
                    aria-label="Data korekty"
                    value={correctionDate}
                    onChange={(e) => setCorrectionDate(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
                  />
                </label>
              </div>
              <label className="block text-xs">
                <span className="text-slate-400">Uwagi</span>
                <textarea
                  aria-label="Uwagi do korekty"
                  rows={2}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
                />
              </label>
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-xs font-semibold text-white hover:bg-amber-500 disabled:opacity-50"
              >
                <Scale className="h-4 w-4" />
                <span>{createMutation.isPending ? 'Rejestrowanie…' : 'Zarejestruj korektę'}</span>
              </button>
            </form>
          )}

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-200">Rejestr korekt</h3>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span>
                  {total === 0
                    ? 'Brak korekt'
                    : `${total} korekt · strona ${page}`}
                </span>
                <button
                  type="button"
                  aria-label="Poprzednia strona"
                  disabled={page <= 1}
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  className="rounded-lg border border-slate-700 px-2.5 py-1.5 hover:bg-slate-800 disabled:opacity-40"
                >
                  ←
                </button>
                <button
                  type="button"
                  aria-label="Następna strona"
                  disabled={!hasNext}
                  onClick={() => setPage((prev) => prev + 1)}
                  className="rounded-lg border border-slate-700 px-2.5 py-1.5 hover:bg-slate-800 disabled:opacity-40"
                >
                  →
                </button>
              </div>
            </div>

            {registerQuery.isLoading ? (
              <div className="p-6 text-center text-sm text-slate-500">Ładowanie rejestru…</div>
            ) : corrections.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">
                Brak zarejestrowanych korekt stanu dla tego badania.
              </div>
            ) : (
              <table className="w-full text-left text-xs text-slate-200" id="stock-corrections-table">
                <thead className="border-b border-slate-800 bg-slate-950/60 text-[11px] uppercase text-slate-400">
                  <tr>
                    <th className="px-3 py-2">Data</th>
                    <th className="px-3 py-2">Zestaw</th>
                    <th className="px-3 py-2">Laboratorium</th>
                    <th className="px-3 py-2">Partia</th>
                    <th className="px-3 py-2 text-right">Korekta</th>
                    <th className="px-3 py-2">Uwagi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/70">
                  {corrections.map((correction) => (
                    <tr key={correction.id}>
                      <td className="px-3 py-2">{correction.correctionDate}</td>
                      <td className="px-3 py-2">
                        {correction.kitName ?? `Zestaw #${correction.labKitId}`}
                      </td>
                      <td className="px-3 py-2">{correction.laboratoryName ?? '—'}</td>
                      <td className="px-3 py-2 font-mono">
                        {correction.labKitExpirationId === null
                          ? 'bez partii'
                          : `#${correction.labKitExpirationId}`}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono font-semibold ${
                          correction.numberOfKits > 0 ? 'text-emerald-400' : 'text-rose-400'
                        }`}
                      >
                        {correction.numberOfKits > 0 ? `+${correction.numberOfKits}` : correction.numberOfKits}
                      </td>
                      <td
                        className="max-w-40 truncate px-3 py-2"
                        title={correction.comments ?? undefined}
                      >
                        {correction.comments ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
